import { Injectable, inject } from '@angular/core';
import { RegistrationStage, SmsRegistrationService } from '@galaxy/sms';
import { ConversationApiService, ConversationChannel, ParticipantType, Widget } from '@vendasta/conversation';
import { Observable, catchError, combineLatest, map, of, shareReplay, switchMap } from 'rxjs';
import { InboxService } from '../inbox.service';
import { ConversationDetail } from '../interface/conversation.interface';
import { ACCOUNT_GROUP_ID_TOKEN, CONVERSATION_COUNTRY_TOKEN } from '../tokens';
import { AlertOptions, ConversationChannelService } from './conversation-channel.abstract';
import { InboxAlertService } from './inbox-alert.service';
import { CONVERSATION_SMS_ENABLED_TOKEN, CONVERSATION_WEB_CHAT_ENABLED_TOKEN } from '../tokens';

@Injectable()
export class ConversationWebchatService extends ConversationChannelService {
  private readonly inboxService = inject(InboxService);
  private readonly country$ = inject(CONVERSATION_COUNTRY_TOKEN);
  private readonly smsRegistrationService = this.inboxService.isBusinessApp ? inject(SmsRegistrationService) : null;
  private readonly inboxAlerter = inject(InboxAlertService);

  private readonly webChatEnabled$ = inject(CONVERSATION_WEB_CHAT_ENABLED_TOKEN);
  private readonly smsEnabled$ = inject(CONVERSATION_SMS_ENABLED_TOKEN);
  private readonly allowedToSendSmsToWebChat$ = combineLatest([this.webChatEnabled$, this.smsEnabled$]).pipe(
    map(([webChatEnabled, smsEnabled]) => webChatEnabled && smsEnabled),
  );

  getChatComposerAlert(
    partnerId: string,
    accountGroupId: string,
    conversationDetail: ConversationDetail,
  ): Observable<AlertOptions | null> {
    if (conversationDetail.conversation.channel !== ConversationChannel.CONVERSATION_CHANNEL_WEB_CHAT) return of(null);

    const anonymousParticipant = conversationDetail?.participants?.find(
      (participant) => participant.participantType === ParticipantType.PARTICIPANT_TYPE_ANONYMOUS,
    );
    if (anonymousParticipant) return of(this.contactInfoMissingError());

    // should only one customer in one specific webchat conversation
    const customerParticipant = conversationDetail?.participants?.find(
      (participant) => participant.participantType === ParticipantType.PARTICIPANT_TYPE_CUSTOMER,
    );
    if (!customerParticipant || (!customerParticipant.phoneNumber && !customerParticipant.email))
      return of(this.contactInfoMissingError());

    return this.country$.pipe(
      switchMap((country) => {
        // partner center or business app and customer has no phone number
        if (!this.smsRegistrationService || !customerParticipant.phoneNumber) {
          return of(
            this.inboxAlerter.smsOrEmailNotAvailableForCapturedLeadError(
              customerParticipant.phoneNumber,
              customerParticipant.email,
              country,
            ),
          );
        }

        const canSendSmsToWebChat$ = combineLatest([
          this.inboxService.SMSNumber$,
          this.smsRegistrationService.registrationStage$,
        ]).pipe(
          map(
            ([smsNumber, registrationStage]) =>
              smsNumber && registrationStage === RegistrationStage.RegistrationComplete,
          ),
        );
        return combineLatest([canSendSmsToWebChat$, this.allowedToSendSmsToWebChat$]).pipe(
          map(([couldSendSms, shouldSendSms]) => {
            if (!couldSendSms || !shouldSendSms) {
              return this.inboxAlerter.smsOrEmailNotAvailableForCapturedLeadError(
                customerParticipant.phoneNumber,
                customerParticipant.email,
                country,
              );
            }
            return null;
          }),
        );
      }),
    );
  }

  private contactInfoMissingError(): AlertOptions {
    return <AlertOptions>{
      title: 'INBOX.WEBCHAT.SETTINGS.WEBCHAT_DISABLED_MISSING_CONTACT_INFO',
    };
  }

  private readonly conversationApiService = inject(ConversationApiService);
  private accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN);

  readonly webchats$: Observable<Widget[]> = this.accountGroupId$.pipe(
    switchMap((accountGroupId) => this.conversationApiService.listWidgets({ namespace: accountGroupId })),
    map((listWidgets) => listWidgets?.widgets),
    shareReplay({ bufferSize: 1, refCount: true }),
    catchError(async () => {
      return [];
    }),
  );

  readonly isAnyWebchatEnabled$ = this.webchats$.pipe(
    map((webchats) => {
      if (webchats) {
        return webchats.some((webchat) => webchat.isEnabled);
      }
      return false;
    }),
  );
}
